// skillgenpro-frontend/src/error-framework/ErrorStates.jsx
//
// The reusable error surfaces. Every one of these is a presentational
// component with no data fetching and no side effects, so dropping one into
// a page can never change that page's behaviour.
//
// They intentionally reuse the app's existing MUI design language (same
// primitives ErrorBoundary.jsx already uses) rather than introducing a new
// styling dependency.
//
// Copy rules applied throughout:
//   * say what happened in the user's terms, never the transport's
//   * never render a stack trace, a raw backend message, or an HTTP code
//     as the headline — those go to the logger and, at most, a small
//     reference id the user can quote to support
//   * always offer a way forward that is not "refresh the browser"

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

const Shell = ({ icon, title, message, errorId, actions, dense }) => (
  <Box
    role="alert"
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      p: dense ? 3 : 5,
      minHeight: dense ? 160 : 320,
      gap: 1,
    }}
  >
    {icon ? (
      <Typography component="div" sx={{ fontSize: dense ? 28 : 40, lineHeight: 1, mb: 1 }} aria-hidden="true">
        {icon}
      </Typography>
    ) : null}
    <Typography variant={dense ? 'subtitle1' : 'h6'} sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460, mb: 1 }}>
      {message}
    </Typography>
    {errorId ? (
      <Typography variant="caption" color="text.disabled" sx={{ mb: 1 }}>
        Reference: {errorId}
      </Typography>
    ) : null}
    {actions && actions.length ? (
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
        {actions.map((a, i) =>
          a ? (
            <Button
              key={a.label + i}
              size={dense ? 'small' : 'medium'}
              variant={i === 0 ? 'contained' : 'text'}
              onClick={a.onClick}
            >
              {a.label}
            </Button>
          ) : null
        )}
      </Box>
    ) : null}
  </Box>
);

const act = (label, onClick) => (typeof onClick === 'function' ? { label, onClick } : null);

/** Generic in-page error. The default when nothing more specific fits. */
export const AppError = ({ title, message, errorId, onRetry, onGoBack, dense }) => (
  <Shell
    icon="⚠️"
    dense={dense}
    title={title || 'Something went wrong'}
    message={message || 'This section could not be displayed. The rest of the app is unaffected.'}
    errorId={errorId}
    actions={[act('Try again', onRetry), act('Go back', onGoBack)]}
  />
);

/** A request failed. `status` is used to pick copy, never shown raw. */
export const ApiError = ({ status, message, errorId, onRetry, onGoBack, dense }) => {
  let title = 'We could not load this';
  let body = message || 'The request did not complete. Please try again in a moment.';
  if (status === 404) {
    title = 'Not found';
    body = message || 'This item no longer exists, or the link is out of date.';
  } else if (status === 409) {
    title = 'That conflicted with a recent change';
    body = message || 'Someone or something else updated this first. Reload to see the latest version.';
  } else if (status === 422) {
    title = 'Some details need attention';
    body = message || 'Part of the information sent was not accepted. Please review and try again.';
  } else if (status === 429) {
    title = 'Slow down for a moment';
    body = message || 'Too many requests in a short time. Please wait a few seconds and try again.';
  }
  return (
    <Shell
      icon="⚠️"
      dense={dense}
      title={title}
      message={body}
      errorId={errorId}
      actions={[act('Try again', onRetry), act('Go back', onGoBack)]}
    />
  );
};

/** 5xx / gateway / timeout — explicitly not the user's fault. */
export const ServerError = ({ errorId, onRetry, onGoHome, dense }) => (
  <Shell
    icon="🛠️"
    dense={dense}
    title="Our side is having trouble"
    message="This is a problem with our service, not with anything you did. It is usually brief — please try again shortly."
    errorId={errorId}
    actions={[act('Try again', onRetry), act('Go home', onGoHome)]}
  />
);

/** 401 / 403. Deliberately does NOT log the user out or redirect. */
export const PermissionDenied = ({ message, errorId, onGoBack, onLogin, dense }) => (
  <Shell
    icon="🔒"
    dense={dense}
    title="You do not have access to this"
    message={message || 'Your account does not have permission to view this section. If you think this is a mistake, contact your administrator.'}
    errorId={errorId}
    actions={[act('Go back', onGoBack), act('Log in again', onLogin)]}
  />
);

/** Last-resort surface used by boundaries for genuinely unknown failures. */
export const UnexpectedError = ({ errorId, onRetry, onReload, onGoHome, dense }) => (
  <Shell
    icon="⚠️"
    dense={dense}
    title="Something unexpected happened"
    message="We have logged the details. You can try this again, or carry on elsewhere — your saved work is unaffected."
    errorId={errorId}
    actions={[act('Try again', onRetry), act('Reload', onReload), act('Go home', onGoHome)]}
  />
);

/** A widget/section failed to load its data. Sized to sit inline. */
export const LoadingError = ({ label, errorId, onRetry, dense = true }) => (
  <Shell
    icon="↻"
    dense={dense}
    title={label ? label + ' could not load' : 'This could not load'}
    message="The content is temporarily unavailable."
    errorId={errorId}
    actions={[act('Retry', onRetry)]}
  />
);

/** AI-specific copy, per the requirement that AI failures never crash. */
export const AiUnavailable = ({ errorId, onRetry, dense = true }) => (
  <Shell
    icon="✨"
    dense={dense}
    title="AI service is temporarily unavailable"
    message="The assistant could not be reached right now. Everything else on this page continues to work normally."
    errorId={errorId}
    actions={[act('Try again', onRetry)]}
  />
);

/**
 * Live online/offline state.
 * navigator.onLine only reports link-layer connectivity, so it is used as a
 * fast negative signal, never as a positive guarantee.
 */
export const useOnlineStatus = () => {
  const [online, setOnline] = React.useState(() => {
    try {
      return typeof navigator === 'undefined' || navigator.onLine !== false;
    } catch (e) {
      return true;
    }
  });

  React.useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    try {
      window.addEventListener('online', goOnline);
      window.addEventListener('offline', goOffline);
    } catch (e) {
      return undefined;
    }
    return () => {
      try {
        window.removeEventListener('online', goOnline);
        window.removeEventListener('offline', goOffline);
      } catch (e) {
        /* no-op */
      }
    };
  }, []);

  return online;
};

export const OfflineState = ({ onRetry, dense }) => (
  <Shell
    icon="📡"
    dense={dense}
    title="You are offline"
    message="We cannot reach the network right now. This page will recover on its own as soon as your connection returns."
    actions={[act('Retry now', onRetry)]}
  />
);

/**
 * Thin top-of-viewport banner. Mounted once at app level; renders nothing
 * at all while online, so it has zero visual or layout cost on the happy
 * path. `onReconnect` fires once per offline -> online transition, which is
 * what drives automatic recovery without a manual refresh.
 */
export const OfflineBanner = ({ onReconnect }) => {
  const online = useOnlineStatus();
  const wasOffline = React.useRef(false);

  React.useEffect(() => {
    if (!online) {
      wasOffline.current = true;
      return;
    }
    if (wasOffline.current) {
      wasOffline.current = false;
      if (typeof onReconnect === 'function') {
        try {
          onReconnect();
        } catch (e) {
          /* recovery must never throw */
        }
      }
    }
  }, [online, onReconnect]);

  if (online) return null;

  return (
    <Box
      role="status"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2000,
        py: 0.75,
        px: 2,
        textAlign: 'center',
        bgcolor: '#b45309',
        color: '#fff',
        fontSize: 13,
        fontWeight: 600,
      }}
    >
      You are offline — we will reconnect automatically.
    </Box>
  );
};

export default {
  AppError, ApiError, ServerError, PermissionDenied, UnexpectedError,
  LoadingError, AiUnavailable, OfflineState, OfflineBanner, useOnlineStatus,
};
